import { createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import {
  DetailedResultAnswer,
  DetailedResultVotes,
  isPoll,
  LocalVotedPoll,
  PollsState,
} from 'features/polls/types';
import {
  pollResultsCleared,
  signalingPollCreated,
  signalingPollDeleted,
  signalingPollEdited,
  signalingPollEnded,
  signalingPollLaunched,
  signalingPollResultsShared,
  signalingPollResultsSharingStopped,
  signalingPollsReceived,
  signalingPollTemplateCreated,
  signalingPollTemplateDeleted,
  signalingPollTemplateEdited,
  signalingPollTemplatesReceived,
  signalingPollVoted,
} from 'features/polls/actions';
import { normalizePolls } from 'features/polls/utils/normalizePolls';
import { requestDetailedPollResults } from 'features/polls/thunks/requestDetailedPollResults';
import { resetPoll } from 'features/polls/utils/resetPoll';

export const initialState: PollsState = {
  exportProcessing: false,
  activePollId: null,
  activePollSessionId: null,
  entities: {},
  ids: [],
  localVotedPolls: {},
  detailedResults: {},
  templates: {
    entities: {},
    ids: [],
  },
};

export const pollsSlice = createSlice({
  name: 'polls',
  initialState,
  reducers: {
    exportPollResultsRequested: (state) => {
      state.exportProcessing = true;
    },
    exportPollResultsFulfilled: (state) => {
      state.exportProcessing = false;
    },
    exportPollResultsRejected: (state) => {
      state.exportProcessing = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(pollResultsCleared, (state, action) => {
        const { canManagePolls } = action.payload;

        state.activePollId = null;
        state.activePollSessionId = null;
        state.localVotedPolls = {};
        state.detailedResults = {};

        if (canManagePolls) {
          for (const id of state.ids) {
            resetPoll(state.entities[id]);
          }
        } else {
          state.entities = {};
          state.ids = [];
        }
      })
      .addCase(signalingPollCreated, (state, action) => {
        const poll = action.payload;

        state.entities[poll.id] = poll;
        state.ids.push(poll.id);
      })
      .addCase(signalingPollsReceived, (state, action) => {
        const { ids, entities, activePollId, activePollSessionId, localVotedPolls } =
          normalizePolls(action.payload.polls);

        // preserve choices when they are retrieved from the server
        if (localVotedPolls) {
          state.localVotedPolls = localVotedPolls;
        }

        // reset previous votes for this poll if poll was relaunched
        if (activePollId && state.activePollSessionId !== activePollSessionId) {
          delete state.localVotedPolls[activePollId];
          delete state.detailedResults[activePollId];
        }

        state.activePollId = activePollId;
        state.activePollSessionId = activePollSessionId;
        state.ids = ids;
        state.entities = entities;
      })
      .addCase(signalingPollLaunched, (state, action) => {
        const { data: poll } = action.payload;

        // preserve choices when they are retrieved from the server
        if (poll.voted) {
          if (typeof poll.voted === 'string') {
            state.localVotedPolls[poll.id] = poll.voted;
          } else {
            state.localVotedPolls[poll.id] = Object.fromEntries(
              poll.voted.map((index) => [index, true])
            );
          }
        }

        // reset previous votes for this poll if poll was relaunched
        if (state.activePollSessionId !== poll.sessionId) {
          delete state.localVotedPolls[poll.id];
          delete state.detailedResults[poll.id];
        }

        state.activePollId = poll.id;
        state.activePollSessionId = poll.sessionId;
        state.entities[poll.id] = poll;

        // Reorder so the active poll is always on top
        state.ids = state.ids.filter((id) => id !== poll.id);
        state.ids.unshift(poll.id);
      })
      .addCase(signalingPollEdited, (state, action) => {
        state.entities[action.payload.id] = action.payload;
      })
      .addCase(signalingPollEnded, (state, action) => {
        const poll = state.entities[action.payload.id];
        poll.active = false;
        poll.ended = true;

        if (state.activePollId === action.payload.id) {
          state.activePollId = null;
          state.activePollSessionId = null;
        }
      })
      .addCase(signalingPollDeleted, (state, action) => {
        delete state.entities[action.payload.id];
        delete state.detailedResults[action.payload.id];
        state.ids = state.ids.filter((id) => id !== action.payload.id);
      })
      .addCase(signalingPollVoted, (state, action) => {
        const { choices, answer, local } = action.payload;

        // populate local votes
        if (local) {
          if (choices) {
            state.localVotedPolls[action.payload.id] = Object.fromEntries(
              choices.map((index) => [index, true])
            );
          } else if (answer) {
            state.localVotedPolls[action.payload.id] = answer;
          }
        }

        const poll = state.entities[action.payload.id];
        poll.votersCount ??= 0;
        poll.votersCount += 1;
        if (poll.choices && choices) {
          for (const index of choices) {
            poll.choices[index].votersCount ??= 0;
            poll.choices[index].votersCount += 1;
          }
        }
      })
      .addCase(signalingPollResultsShared, (state, action) => {
        if (isPoll(action.payload.data)) {
          const poll = action.payload.data;
          state.entities[poll.id] = poll;
        } else {
          const poll = state.entities[action.payload.data.id];
          poll.shared = true;
        }
      })
      .addCase(signalingPollResultsSharingStopped, (state, action) => {
        const poll = state.entities[action.payload.id];
        poll.shared = false;
      })
      .addCase(requestDetailedPollResults.pending, (state, action) => {
        const pollId = action.meta.arg;
        state.detailedResults[pollId] = { status: 'loading' };
      })
      .addCase(requestDetailedPollResults.rejected, (state, action) => {
        const pollId = action.meta.arg;
        state.detailedResults[pollId] = { status: 'error' };
      })
      .addCase(requestDetailedPollResults.fulfilled, (state, action) => {
        const pollId = action.meta.arg;
        const votes: DetailedResultVotes = {};
        const answers: DetailedResultAnswer[] = [];

        for (const vote of action.payload.votes) {
          if (vote.choices) {
            for (const choiceIndex of vote.choices) {
              if (!votes[choiceIndex]) {
                votes[choiceIndex] = [];
              }
              votes[choiceIndex].push(vote.voter);
            }
          } else if (vote.answer) {
            answers.push({
              voter: vote.voter,
              answer: vote.answer,
            });
          }
        }

        const hasVotes = Object.keys(votes).length > 0;
        const hasAnswers = answers.length > 0;

        state.detailedResults[pollId] = {
          status: 'success',
          ...(hasVotes && { votes }),
          ...(hasAnswers && { answers }),
        };
      })
      .addCase(signalingPollTemplatesReceived, (state, action) => {
        const { templates } = action.payload;

        state.templates.entities = {};
        state.templates.ids = [];

        for (const template of templates) {
          state.templates.entities[template.id] = template;
          state.templates.ids.push(template.id);
        }
      })
      .addCase(signalingPollTemplateCreated, (state, action) => {
        const template = action.payload;

        state.templates.entities[template.id] = template;
        state.templates.ids.unshift(template.id);
      })
      .addCase(signalingPollTemplateEdited, (state, action) => {
        const template = action.payload;

        state.templates.entities[template.id] = template;
      })
      .addCase(signalingPollTemplateDeleted, (state, action) => {
        const { id } = action.payload;

        delete state.templates.entities[id];
        state.templates.ids = state.templates.ids.filter((tid) => tid !== id);
      });
  },
});

export const { exportPollResultsRejected, exportPollResultsRequested, exportPollResultsFulfilled } =
  pollsSlice.actions;

export const selectPoll = (state: RootState, id: string) => state.polls.entities[id];

export const selectPollEntities = (state: RootState) => state.polls.entities;

export const selectLocalVotedPoll = (state: RootState, id: string): LocalVotedPoll | undefined =>
  state.polls.localVotedPolls[id];

export const selectPollDetailedResults = (state: RootState, id: string) =>
  state.polls.detailedResults[id];

export const selectActivePollId = (state: RootState) => state.polls.activePollId;

export const selectPollTemplates = (state: RootState) => state.polls.templates.entities;

export const selectPollTemplateIds = (state: RootState) => state.polls.templates.ids;

export const selectPollTemplateById = (state: RootState, id: string) =>
  state.polls.templates.entities[id];

export const selectAllPollTemplates = createSelector(
  [selectPollTemplates, selectPollTemplateIds],
  (entities, ids) => ids.map((id) => entities[id])
);

export const selectHasPollTemplates = (state: RootState) => state.polls.templates.ids.length > 0;

export default pollsSlice.reducer;
